/* eslint-disable no-console */
import { useState, useEffect, useCallback, useRef } from "react";
import toast from "react-hot-toast";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { CUENTAS_ER, TIPOS_ACTIVO, DESTINO_TIPOS, getVidaUtilPorTipo } from "../../constants/clasificacionContable";
import { useAuthContext } from "../../context/auth";
import { crearActivo, eliminarActivo } from "../../utils/activos";
import { vincularFacturaRecibida, ignorarFacturaRecibida, reactivarFactura } from "../../utils/conciliacionSII";
import { actualizarProveedor } from "../../utils/proveedores";
import { client } from "../../utils/supabase";

// ─── Helpers ─────────────────────────────────────────────

const formatMonto = (monto) => {
  if (monto == null) return "-";
  return `$ ${Number(monto).toLocaleString("es-CL")}`;
};

const formatRut = (rut) => {
  if (!rut) return "-";
  const [cuerpo, dv] = rut.split("-");
  if (!dv) return rut;
  return `${Number(cuerpo).toLocaleString("es-CL")}-${dv}`;
};

const formatFecha = (fechaStr) => {
  if (!fechaStr) return "-";
  if (fechaStr.includes("T")) fechaStr = fechaStr.substring(0, 10);
  const [year, month, day] = fechaStr.split("-");
  return `${day}/${month}/${year}`;
};

// Agrupar CUENTAS_ER por grupo
const CUENTAS_POR_GRUPO = {};
CUENTAS_ER.forEach((c) => {
  if (!CUENTAS_POR_GRUPO[c.grupo]) CUENTAS_POR_GRUPO[c.grupo] = [];
  CUENTAS_POR_GRUPO[c.grupo].push(c);
});

const LINEA_VACIA = {
  tipo_activo: "",
  descripcion: "",
  numero_serie: "",
  cantidad: 1,
  costo_unitario: 0,
  destino_tipo: "",
  destino_id: "",
  // Empleado usa columna aparte (uuid) — destino_id es INTEGER y no admite el uuid del empleado.
  destino_empleado_id: "",
};

// ─── Componente Modal ────────────────────────────────────

const AsignarRecibidaModal = ({ isOpen, toggle, factura, onAsignada }) => {
  const { pais, id: userId } = useAuthContext();

  const [cuentaER, setCuentaER] = useState("");
  const [tipoContable, setTipoContable] = useState("");
  const [recordar, setRecordar] = useState(false);
  const [proveedor, setProveedor] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [plazopago, setPlazopago] = useState("");

  // Empleados activos del país — para el selector de destino "empleado" de las líneas de activo.
  const [empleadosDestino, setEmpleadosDestino] = useState([]);

  // Líneas de activo
  const [lineasActivo, setLineasActivo] = useState([]);
  const [lineasExpandidas, setLineasExpandidas] = useState({});
  const [edificios, setEdificios] = useState([]);
  const [proyectosCarteleria, setProyectosCarteleria] = useState([]);

  // Activos ya creados para esta factura (vista de solo-lectura cuando está vinculada).
  const [activosVinculados, setActivosVinculados] = useState([]);
  const [desvinculando, setDesvinculando] = useState(false);
  // Confirmación inline del Desvincular (en vez de window.confirm, que en algunos navegadores
  // queda suprimido y hacía que el botón "no hiciera nada").
  const [confirmandoDesvincular, setConfirmandoDesvincular] = useState(false);
  // Se acaba de desvincular la clasificación dentro de esta sesión del modal: fuerza el
  // modo editable aunque el prop `factura` (stale) siga diciendo tipo_contable != null.
  const [desvinculada, setDesvinculada] = useState(false);
  // Última factura por la que se inicializó el formulario. Evita que un re-render del padre
  // que re-pase el mismo objeto `factura` (otra referencia, misma id) re-dispare el efecto de
  // apertura y pise el estado local (p.ej. desvinculada=false) tras desvincular.
  const facturaInicializadaId = useRef(null);

  // Cargar proveedor (por rut_ruc) + contexto de selectores.
  const cargarContexto = useCallback(async () => {
    if (!factura || !pais) return;
    setCargando(true);
    try {
      // Buscar proveedor por rut_ruc
      const { data: proveedores } = await client
        .from("proveedores")
        .select("id, nombre, rut_ruc, cuenta_er_default, tipo_contable_default")
        .eq("rut_ruc", factura.rut_emisor)
        .eq("pais", pais)
        .limit(1);

      const prov = proveedores?.[0] || null;
      setProveedor(prov);

      // Pre-seleccionar defaults del proveedor SOLO si la factura aún no tiene valores propios
      // (modo Editar: respetar lo que ya quedó persistido en facturas_sii_recibidas).
      if (prov?.cuenta_er_default && !factura.cuenta_er) {
        setCuentaER(prov.cuenta_er_default);
      }
      if (prov?.tipo_contable_default && !factura.tipo_contable) {
        setTipoContable(prov.tipo_contable_default);
      }

      // Cargar edificios, proyectos cartelería y empleados activos para los selectores de
      // destino de las líneas de activo.
      const [{ data: edifData }, { data: cartData }, { data: empData }] = await Promise.all([
        client.from("edificios").select("id, edificio").eq("pais", pais).order("edificio"),
        client
          .from("proyectos_carteleria")
          .select("id, nombre_proyecto, referencia_id")
          .eq("pais", pais)
          .order("nombre_proyecto"),
        client
          .from("empleados")
          .select("id, usuario_id, nombre, apellido, egreso_fecha")
          .eq("pais", pais)
          .is("egreso_fecha", null)
          .order("nombre"),
      ]);
      setEdificios(edifData || []);
      setProyectosCarteleria(cartData || []);
      setEmpleadosDestino(empData || []);

      // Activos ya creados para esta factura (para mostrarlos en la vista de solo-lectura).
      const { data: activosData } = await client
        .from("activos")
        .select("id, tipo_activo, descripcion, cantidad, costo_unitario, destino_tipo, destino_id, destino_empleado_id")
        .eq("factura_sii_recibida_id", factura.id)
        .eq("eliminado", false);
      setActivosVinculados(activosData || []);
    } catch (err) {
      console.error("Error cargando contexto:", err);
    } finally {
      setCargando(false);
    }
  }, [factura, pais]);

  useEffect(() => {
    if (!isOpen) {
      // Al cerrar, permitir que la próxima apertura re-inicialice (incluso la misma factura).
      facturaInicializadaId.current = null;
      return;
    }
    if (!factura) return;
    // Inicializar SOLO al abrir o al cambiar de factura (por id). Si el padre re-pasa el mismo
    // objeto con otra referencia, no re-inicializamos: así no se pisa el estado tras desvincular.
    if (factura.id === facturaInicializadaId.current) return;
    facturaInicializadaId.current = factura.id;

    setCuentaER(factura.cuenta_er || "");
    setTipoContable(factura.tipo_contable || "");
    setRecordar(false);
    setConfirmando(false);
    setModoEdicion(false);
    setProveedor(null);
    setLineasActivo([]);
    setActivosVinculados([]);
    setDesvinculada(false);
    setConfirmandoDesvincular(false);
    setPlazopago(factura.plazopago || "");
    cargarContexto();
  }, [isOpen, factura, cargarContexto]);

  // Derivar categoría ER del valor seleccionado
  const getCategoriaER = (valor) => {
    const cuenta = CUENTAS_ER.find((c) => c.value === valor);
    if (!cuenta) return null;
    if (cuenta.grupo === "Costos Fijos") return "costo_fijo";
    if (cuenta.grupo === "Costos Variables") return "costo_variable";
    return "otro";
  };

  // ─── Helpers líneas de activo ──────────────────────────

  const agregarLinea = () => setLineasActivo([...lineasActivo, { ...LINEA_VACIA }]);

  const eliminarLinea = (idx) => setLineasActivo(lineasActivo.filter((_, i) => i !== idx));

  const actualizarLinea = (idx, campo, valor) => {
    setLineasActivo((prev) =>
      prev.map((l, i) => {
        if (i !== idx) return l;
        const updated = { ...l, [campo]: valor };
        // Al cambiar destino_tipo, resetear ambos ids de destino (integer y uuid empleado)
        if (campo === "destino_tipo") {
          updated.destino_id = "";
          updated.destino_empleado_id = "";
        }
        return updated;
      })
    );
  };

  // ─── Totalizador ──────────────────────────────────────

  const totalLineas = lineasActivo.reduce(
    (sum, l) => sum + (Number(l.cantidad) || 0) * (Number(l.costo_unitario) || 0),
    0
  );
  const montoNeto = Number(factura?.monto_neto) || 0;
  const diferenciaOk = lineasActivo.length > 0 && Math.abs(totalLineas - montoNeto) < 1;

  // ─── Validación líneas ────────────────────────────────

  const validarLineas = () => {
    if (lineasActivo.length === 0) {
      toast.error("Agregá al menos una línea de activo");
      return false;
    }
    for (let i = 0; i < lineasActivo.length; i++) {
      const l = lineasActivo[i];
      if (!l.tipo_activo) {
        toast.error(`Línea ${i + 1}: seleccioná un tipo de activo`);
        return false;
      }
      if (!l.cantidad || l.cantidad < 1) {
        toast.error(`Línea ${i + 1}: cantidad debe ser al menos 1`);
        return false;
      }
      if (!l.costo_unitario || l.costo_unitario <= 0) {
        toast.error(`Línea ${i + 1}: costo unitario debe ser mayor a 0`);
        return false;
      }
    }
    return true;
  };

  // ─── Asignar ──────────────────────────────────────────

  const handleAsignar = async () => {
    // Si la factura ya tiene pago (vinculada), este modal solo agrega la clasificación
    // contable: no se pide ni se persiste "Pago estimado" (el pago ya existe).
    const tienePago = factura.estado_vinculacion === "vinculada";

    if (tipoContable === "gasto" && !cuentaER) {
      toast.error("Seleccioná una cuenta del ER");
      return;
    }
    if (tipoContable === "activo" && !validarLineas()) return;
    if (!tienePago && !plazopago) {
      toast.error("Ingresá una fecha de pago estimada para continuar");
      return;
    }

    const moneda = pais === "Chile" ? "CLP" : "PEN";

    setGuardando(true);
    try {
      // 1. Vincular la factura SII recibida
      await vincularFacturaRecibida(factura.id, {
        proveedor_id: proveedor?.id || factura.proveedor_id || null,
        cuenta_er: cuentaER,
        cuenta_er_categoria: getCategoriaER(cuentaER),
        tipo_contable: tipoContable,
        usuario_id: userId,
      });

      // Persistir plazopago solo si la factura aún no tiene pago (no vinculada).
      if (!tienePago) {
        await client.from("facturas_sii_recibidas").update({ plazopago }).eq("id", factura.id);
      }

      // 2. Si es activo, crear activos
      if (tipoContable === "activo" && lineasActivo.length > 0) {
        for (const l of lineasActivo) {
          // eslint-disable-next-line no-await-in-loop
          await crearActivo({
            pais,
            fecha_compra: factura.fecha_emision?.substring(0, 10) || new Date().toISOString().substring(0, 10),
            tipo_activo: l.tipo_activo,
            descripcion: l.descripcion || factura.razon_social_emisor,
            numero_serie: l.numero_serie || null,
            numero_factura: String(factura.folio),
            cantidad: Number(l.cantidad) || 1,
            costo_unitario: Number(l.costo_unitario) || 0,
            moneda,
            vida_util_meses: getVidaUtilPorTipo(l.tipo_activo),
            valor_residual: 0,
            estado: "activo",
            destino_tipo: l.destino_tipo || null,
            // Empleado → destino_empleado_id (uuid); edificio/cartelería → destino_id (integer).
            destino_id: l.destino_tipo === "empleado" ? null : l.destino_id || null,
            destino_empleado_id: l.destino_tipo === "empleado" ? l.destino_empleado_id || null : null,
            proveedor_id: proveedor?.id || null,
            factura_sii_recibida_id: factura.id,
          });
        }
      }

      // 3. Recordar cuenta_er_default para el proveedor
      if (recordar && proveedor) {
        await actualizarProveedor(proveedor.id, {
          cuenta_er_default: cuentaER,
          tipo_contable_default: tipoContable,
        });
      }

      toast.success("Factura asignada correctamente");
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error asignando:", err);
      toast.error("Error al asignar factura");
    } finally {
      setGuardando(false);
    }
  };

  const handleIgnorar = async () => {
    setGuardando(true);
    try {
      await ignorarFacturaRecibida(factura.id, userId);
      toast.success("DTE marcado como ignorado");
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error ignorando:", err);
      toast.error("Error al ignorar DTE");
    } finally {
      setGuardando(false);
    }
  };

  const handleReactivar = async () => {
    setGuardando(true);
    try {
      await reactivarFactura(factura.id, "recibida");
      toast.success("Factura reactivada");
      onAsignada?.();
      toggle();
    } catch (err) {
      console.error("Error reactivando:", err);
      toast.error("Error al reactivar factura");
    } finally {
      setGuardando(false);
    }
  };

  // Desvincular SOLO la clasificación contable de la factura (no toca el pago bancario:
  // MBF / movimientos_caja / movimientos_bancarios se manejan desde Flujo de Caja).
  // - Limpia tipo_contable/cuenta_er/cuenta_er_categoria + vinculado_* y soft-elimina los
  //   activos creados para esta factura (+ borra sus filas en factura_activo_lineas) para que
  //   re-clasificarla no duplique activos.
  // - estado_vinculacion: si la factura tiene pago bancario (fila en movimiento_bancario_facturas)
  //   queda 'vinculada' (el pago sigue vivo); si no, vuelve a 'pendiente'.
  const handleDesvincular = async () => {
    setDesvinculando(true);
    try {
      // 1. ¿Tiene pago bancario? Determina el estado_vinculacion final. Best-effort: si la
      //    consulta falla, conservamos 'vinculada' (no perder el pago).
      let tienePagoBancario = true;
      try {
        const { data: mbfRows, error: errMbf } = await client
          .from("movimiento_bancario_facturas")
          .select("id")
          .eq("factura_tipo", "sii_recibida")
          .eq("factura_id", factura.id);
        if (errMbf) throw errMbf;
        tienePagoBancario = (mbfRows || []).length > 0;
      } catch (err) {
        console.error("desvincular: error consultando MBF (se conserva vinculada):", err);
      }

      // 2. Reset de la clasificación contable en la fila SII. PASO CRÍTICO: si esto falla,
      //    abortamos (no tocamos la UI). Si funciona, la factura ya quedó sin clasificación.
      const { error: errSii } = await client
        .from("facturas_sii_recibidas")
        .update({
          tipo_contable: null,
          cuenta_er: null,
          cuenta_er_categoria: null,
          vinculado_por: null,
          vinculado_at: null,
          estado_vinculacion: tienePagoBancario ? "vinculada" : "pendiente",
        })
        .eq("id", factura.id);
      if (errSii) throw errSii;

      // 3. Refrescar el estado local INMEDIATAMENTE → el re-render muestra el formulario
      //    editable. No cerramos el modal. Se hace ANTES de la limpieza de activos para que un
      //    fallo en esa limpieza no impida pasar a modo editable.
      setDesvinculada(true);
      setTipoContable("");
      setCuentaER("");
      setLineasActivo([]);
      setActivosVinculados([]);
      setConfirmando(false);
      setConfirmandoDesvincular(false);
      toast.success("Clasificación desvinculada. Ya podés reclasificar.");

      // 4. Limpieza best-effort (no bloquea la UI): soft-delete de activos + borrar líneas.
      try {
        const { data: activosFactura } = await client
          .from("activos")
          .select("id")
          .eq("factura_sii_recibida_id", factura.id)
          .eq("eliminado", false);
        for (const a of activosFactura || []) {
          // eslint-disable-next-line no-await-in-loop
          await eliminarActivo(a.id);
        }
      } catch (err) {
        console.error("desvincular: error soft-deleting activos:", err);
      }
      try {
        await client.from("factura_activo_lineas").delete().eq("factura_sii_recibida_id", factura.id);
      } catch (err) {
        console.error("desvincular: error borrando factura_activo_lineas:", err);
      }
    } catch (err) {
      console.error("Error desvinculando:", err);
      toast.error("Error al desvincular: " + (err.message || ""));
    } finally {
      setDesvinculando(false);
    }
  };

  if (!factura) return null;

  const esIgnorada = factura.estado_vinculacion === "ignorada";
  // Factura ya pagada/vinculada (vía VincularMovimientoModal): el pago ya existe, así que el
  // modal no pide "Pago estimado" ni lo persiste al clasificar.
  const esVinculada = factura.estado_vinculacion === "vinculada";
  // Modo solo-lectura: solo cuando la factura está vinculada Y ya tiene clasificación contable
  // (y no se acaba de desvincular en esta sesión). Una vinculada SIN clasificar (tipo_contable
  // null) muestra el formulario editable para poder clasificarla.
  const esVinculadaConClasificacion = esVinculada && !!factura.tipo_contable && !desvinculada;
  const cuentaLabel = CUENTAS_ER.find((c) => c.value === cuentaER)?.label;
  // Modo B (rápido) solo aplica al asignar una factura pendiente. En modo Editar
  // (factura.cuenta_er ya seteado) se salta a Modo C para que el usuario vea y edite
  // los valores reales persistidos en la fila.
  const tieneDefaults = proveedor?.cuenta_er_default && proveedor?.tipo_contable_default && !factura.cuenta_er;
  const modoRapido = tieneDefaults && !modoEdicion && !esVinculada;
  const defaultCuentaLabel =
    CUENTAS_ER.find((c) => c.value === proveedor?.cuenta_er_default)?.label || proveedor?.cuenta_er_default;

  // ─── Opciones destino por línea ───────────────────────

  const getDestinoOpciones = (destTipo) => {
    if (destTipo === "edificio") return edificios.map((e) => ({ value: e.id, label: e.edificio }));
    if (destTipo === "carteleria") {
      return proyectosCarteleria.map((p) => ({ value: p.id, label: p.nombre_proyecto || p.referencia_id }));
    }
    if (destTipo === "empleado") {
      return empleadosDestino.map((emp) => ({ value: emp.id, label: `${emp.nombre} ${emp.apellido || ""}`.trim() }));
    }
    return [];
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {esVinculadaConClasificacion
          ? "DTE vinculado"
          : factura.estado_vinculacion === "pendiente"
            ? "Asignar DTE"
            : "Editar DTE"}{" "}
        — Folio {factura.folio} &middot; {factura.razon_social_emisor}
      </ModalHeader>
      <ModalBody>
        {/* Banner ignorada */}
        {esIgnorada && (
          <div
            style={{
              background: "#fff3cd",
              color: "#856404",
              padding: "10px 14px",
              borderRadius: 6,
              marginBottom: 14,
              fontSize: "0.82rem",
              fontWeight: 600,
            }}
          >
            Esta factura está marcada como descartada
          </div>
        )}

        {/* Detalle del DTE — una línea compacta */}
        <div
          style={{
            background: "#f8f9fa",
            padding: "8px 14px",
            borderRadius: 6,
            marginBottom: 14,
            fontSize: "0.8rem",
            display: "flex",
            flexWrap: "wrap",
            gap: "4px 24px",
            alignItems: "center",
          }}
        >
          <span>
            <strong>Fecha:</strong> {formatFecha(factura.fecha_emision)}
          </span>
          <span>
            <strong>RUT:</strong> {formatRut(factura.rut_emisor)}
          </span>
          <span>
            <strong>Neto:</strong> {formatMonto(factura.monto_neto)}
          </span>
          <span>
            <strong>IVA:</strong> {formatMonto(factura.monto_iva)}
          </span>
          <span>
            <strong>Total:</strong> {formatMonto(factura.monto_total)}
          </span>
        </div>

        {!esIgnorada && cargando && (
          <div style={{ textAlign: "center", padding: 16, color: "#999" }}>Cargando datos del proveedor...</div>
        )}

        {/* Modo rápido: proveedor con cuenta recordada */}
        {!esIgnorada && !cargando && modoRapido && (
          <div
            style={{
              padding: "14px 16px",
              background: "#eafaf1",
              borderRadius: 6,
              border: "1px solid #a9dfbf",
              fontSize: "0.85rem",
            }}
          >
            <div style={{ marginBottom: 10 }}>
              <strong>{proveedor.nombre}</strong>
              {" se asigna como "}
              <strong style={{ textTransform: "capitalize" }}>{proveedor.tipo_contable_default}</strong>
              {" en "}
              <strong>{defaultCuentaLabel}</strong>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <label style={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                Pago estimado <span style={{ color: "#e74c3c" }}>*</span>
              </label>
              <input
                type="date"
                value={plazopago}
                onChange={(e) => setPlazopago(e.target.value)}
                style={{
                  height: 30,
                  fontSize: "0.78rem",
                  padding: "4px 8px",
                  border: `1px solid ${!plazopago ? "#e74c3c" : "#aed6f1"}`,
                  borderRadius: 4,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <Button color="success" size="sm" style={{ minWidth: 100 }} onClick={handleAsignar} disabled={guardando}>
                {guardando ? "Guardando..." : "Confirmar"}
              </Button>
              <Button color="warning" size="sm" style={{ minWidth: 100 }} onClick={() => setModoEdicion(true)}>
                Cambiar
              </Button>
              <Button
                color="danger"
                size="sm"
                style={{ minWidth: 100 }}
                onClick={async () => {
                  await actualizarProveedor(proveedor.id, {
                    cuenta_er_default: null,
                    tipo_contable_default: null,
                  });
                  toast.success(`Asociación olvidada para ${proveedor.nombre}`);
                  setProveedor({ ...proveedor, cuenta_er_default: null, tipo_contable_default: null });
                  setModoEdicion(true);
                }}
              >
                Olvidar
              </Button>
            </div>
          </div>
        )}

        {!esIgnorada && !cargando && !modoRapido && !esVinculadaConClasificacion && (
          <>
            {/* Proveedor identificado */}
            {proveedor && (
              <div
                style={{
                  fontSize: "0.78rem",
                  marginBottom: 12,
                  padding: "8px 12px",
                  background: "#d5f5e3",
                  borderRadius: 4,
                  color: "#27ae60",
                }}
              >
                Proveedor identificado: <strong>{proveedor.nombre}</strong> (ID {proveedor.id})
                {proveedor.cuenta_er_default && (
                  <span>
                    {" "}
                    — Cuenta habitual:{" "}
                    <strong>
                      {CUENTAS_ER.find((c) => c.value === proveedor.cuenta_er_default)?.label ||
                        proveedor.cuenta_er_default}
                    </strong>
                  </span>
                )}
              </div>
            )}

            {/* Selector Tipo Contable (arriba del todo) */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                Tipo contable
              </label>
              <div style={{ display: "flex", gap: 16, fontSize: "0.82rem" }}>
                <label style={{ cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="tipoContable"
                    value="gasto"
                    checked={tipoContable === "gasto"}
                    onChange={() => {
                      setTipoContable("gasto");
                      setLineasActivo([]);
                    }}
                    style={{ marginRight: 4 }}
                  />
                  Gasto
                </label>
                <label style={{ cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="tipoContable"
                    value="activo"
                    checked={tipoContable === "activo"}
                    onChange={() => {
                      setTipoContable("activo");
                      if (lineasActivo.length === 0) agregarLinea();
                    }}
                    style={{ marginRight: 4 }}
                  />
                  Activo
                </label>
              </div>
            </div>

            {/* Selector Cuenta ER — solo para gastos */}
            {tipoContable === "gasto" && (
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: "0.75rem", fontWeight: 600, display: "block", marginBottom: 4 }}>
                  Cuenta del Estado de Resultados
                </label>
                <select
                  value={cuentaER}
                  onChange={(e) => setCuentaER(e.target.value)}
                  style={{
                    width: "100%",
                    height: 36,
                    fontSize: "0.82rem",
                    padding: "4px 10px",
                    border: "1px solid #ced4da",
                    borderRadius: 4,
                  }}
                >
                  <option value="">— Seleccionar cuenta —</option>
                  {Object.entries(CUENTAS_POR_GRUPO).map(([grupo, cuentas]) => (
                    <optgroup key={grupo} label={grupo}>
                      {cuentas.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            )}

            {/* ─── Sección Líneas de Activo (compacta) ──────── */}
            {tipoContable === "activo" && (
              <div
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  padding: "10px 12px",
                  marginBottom: 12,
                  background: "#fcfcfd",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span style={{ fontWeight: 600, fontSize: "0.82rem" }}>Líneas de activo ({lineasActivo.length})</span>
                  <Button color="dark" outline size="sm" onClick={agregarLinea} style={{ fontSize: "0.75rem" }}>
                    + Agregar línea
                  </Button>
                </div>

                {/* Cabecera columnas */}
                {lineasActivo.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                      fontSize: "0.68rem",
                      color: "#888",
                      fontWeight: 600,
                      padding: "0 2px 4px",
                    }}
                  >
                    <span style={{ width: 200, minWidth: 200 }}>Tipo *</span>
                    <span style={{ width: 70, minWidth: 70, textAlign: "center" }}>Cant. *</span>
                    <span style={{ width: 120, minWidth: 120 }}>Costo unit. *</span>
                    <span style={{ width: 180, minWidth: 180 }}>Destino</span>
                    <span style={{ width: 22 }} />
                  </div>
                )}

                {lineasActivo.map((linea, idx) => {
                  const destinoOpciones = getDestinoOpciones(linea.destino_tipo);
                  const expanded = !!lineasExpandidas[idx];
                  const vidaUtil = linea.tipo_activo ? getVidaUtilPorTipo(linea.tipo_activo) : null;

                  return (
                    <div key={idx} style={{ marginBottom: 4 }}>
                      {/* Fila principal compacta */}
                      <div style={{ display: "flex", gap: 6, alignItems: "center", fontSize: "0.78rem" }}>
                        {/* Tipo activo */}
                        <select
                          value={linea.tipo_activo}
                          onChange={(e) => actualizarLinea(idx, "tipo_activo", e.target.value)}
                          style={{
                            width: 200,
                            minWidth: 200,
                            height: 30,
                            fontSize: "0.76rem",
                            border: "1px solid #ced4da",
                            borderRadius: 3,
                            padding: "2px 4px",
                          }}
                        >
                          <option value="">— Tipo —</option>
                          {TIPOS_ACTIVO.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>

                        {/* Cantidad */}
                        <input
                          type="number"
                          min={1}
                          value={linea.cantidad}
                          onChange={(e) => actualizarLinea(idx, "cantidad", parseInt(e.target.value, 10) || 1)}
                          style={{
                            width: 70,
                            minWidth: 70,
                            height: 30,
                            fontSize: "0.76rem",
                            border: "1px solid #ced4da",
                            borderRadius: 3,
                            padding: "2px 6px",
                            textAlign: "center",
                          }}
                        />

                        {/* Costo unitario */}
                        <input
                          type="number"
                          min={0}
                          value={linea.costo_unitario}
                          onChange={(e) => actualizarLinea(idx, "costo_unitario", parseFloat(e.target.value) || 0)}
                          style={{
                            width: 120,
                            minWidth: 120,
                            height: 30,
                            fontSize: "0.76rem",
                            border: "1px solid #ced4da",
                            borderRadius: 3,
                            padding: "2px 6px",
                          }}
                        />

                        {/* Destino — selector combinado tipo + id */}
                        {!linea.destino_tipo || destinoOpciones.length === 0 ? (
                          <select
                            value={linea.destino_tipo}
                            onChange={(e) => actualizarLinea(idx, "destino_tipo", e.target.value)}
                            style={{
                              width: 180,
                              minWidth: 180,
                              height: 30,
                              fontSize: "0.76rem",
                              border: "1px solid #ced4da",
                              borderRadius: 3,
                              padding: "2px 4px",
                            }}
                          >
                            {DESTINO_TIPOS.map((d) => (
                              <option key={d.value} value={d.value}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div style={{ width: 180, minWidth: 180, display: "flex", gap: 3 }}>
                            <select
                              value={linea.destino_tipo === "empleado" ? linea.destino_empleado_id : linea.destino_id}
                              onChange={(e) =>
                                linea.destino_tipo === "empleado"
                                  ? actualizarLinea(idx, "destino_empleado_id", e.target.value || "")
                                  : actualizarLinea(idx, "destino_id", Number(e.target.value) || "")
                              }
                              style={{
                                flex: 1,
                                height: 30,
                                fontSize: "0.76rem",
                                border: "1px solid #ced4da",
                                borderRadius: 3,
                                padding: "2px 4px",
                              }}
                            >
                              <option value="">
                                {linea.destino_tipo === "edificio"
                                  ? "Edificio..."
                                  : linea.destino_tipo === "empleado"
                                    ? "Empleado..."
                                    : "Proyecto..."}
                              </option>
                              {destinoOpciones.map((o) => (
                                <option key={o.value} value={o.value}>
                                  {o.label}
                                </option>
                              ))}
                            </select>
                            <button
                              onClick={() => actualizarLinea(idx, "destino_tipo", "")}
                              title="Cambiar destino"
                              style={{
                                background: "none",
                                border: "none",
                                color: "#999",
                                cursor: "pointer",
                                fontSize: "0.7rem",
                                padding: "0 2px",
                              }}
                            >
                              &larr;
                            </button>
                          </div>
                        )}

                        {/* Botón eliminar */}
                        <button
                          onClick={() => eliminarLinea(idx)}
                          disabled={lineasActivo.length <= 1}
                          style={{
                            width: 22,
                            minWidth: 22,
                            background: "none",
                            border: "none",
                            color: lineasActivo.length > 1 ? "#e74c3c" : "#ccc",
                            cursor: lineasActivo.length > 1 ? "pointer" : "default",
                            fontSize: "1rem",
                            padding: 0,
                            lineHeight: 1,
                          }}
                          title="Eliminar línea"
                        >
                          &times;
                        </button>
                      </div>

                      {/* Link "ver más" + campos expandibles */}
                      <div style={{ paddingLeft: 2, marginTop: 2 }}>
                        <button
                          onClick={() => setLineasExpandidas((prev) => ({ ...prev, [idx]: !prev[idx] }))}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#2980b9",
                            cursor: "pointer",
                            fontSize: "0.7rem",
                            padding: 0,
                          }}
                        >
                          {expanded ? "ocultar" : "ver más"}
                          {vidaUtil && !expanded ? ` (${vidaUtil}m)` : ""}
                        </button>

                        {expanded && (
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              marginTop: 4,
                              marginBottom: 2,
                              fontSize: "0.76rem",
                            }}
                          >
                            <div style={{ flex: 2 }}>
                              <label style={{ fontSize: "0.68rem", color: "#888" }}>Descripción</label>
                              <input
                                type="text"
                                value={linea.descripcion}
                                onChange={(e) => actualizarLinea(idx, "descripcion", e.target.value)}
                                placeholder="Descripción del activo"
                                style={{
                                  width: "100%",
                                  height: 28,
                                  fontSize: "0.76rem",
                                  border: "1px solid #ced4da",
                                  borderRadius: 3,
                                  padding: "2px 6px",
                                }}
                              />
                            </div>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: "0.68rem", color: "#888" }}>N° Serie</label>
                              <input
                                type="text"
                                value={linea.numero_serie}
                                onChange={(e) => actualizarLinea(idx, "numero_serie", e.target.value)}
                                placeholder="Opcional"
                                style={{
                                  width: "100%",
                                  height: 28,
                                  fontSize: "0.76rem",
                                  border: "1px solid #ced4da",
                                  borderRadius: 3,
                                  padding: "2px 6px",
                                }}
                              />
                            </div>
                            <div style={{ width: 80 }}>
                              <label style={{ fontSize: "0.68rem", color: "#888" }}>Vida útil</label>
                              <input
                                type="text"
                                readOnly
                                value={vidaUtil ? `${vidaUtil}m` : "—"}
                                style={{
                                  width: "100%",
                                  height: 28,
                                  fontSize: "0.76rem",
                                  border: "1px solid #e9ecef",
                                  borderRadius: 3,
                                  padding: "2px 6px",
                                  background: "#f5f5f5",
                                  color: "#888",
                                }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Totalizador */}
                {lineasActivo.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "6px 10px",
                      borderRadius: 4,
                      background: diferenciaOk ? "#d5f5e3" : "#fdecea",
                      fontSize: "0.78rem",
                      fontWeight: 600,
                      marginTop: 6,
                    }}
                  >
                    <span>Total: {formatMonto(totalLineas)}</span>
                    <span>Neto: {formatMonto(montoNeto)}</span>
                    <span style={{ color: diferenciaOk ? "#27ae60" : "#e74c3c" }}>
                      {diferenciaOk ? "Coincide" : `Dif: ${formatMonto(Math.abs(totalLineas - montoNeto))}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Recordar para proveedor */}
            {proveedor && (
              <div style={{ marginBottom: 8 }}>
                <label style={{ fontSize: "0.78rem", cursor: "pointer" }}>
                  <input
                    type="checkbox"
                    checked={recordar}
                    onChange={(e) => setRecordar(e.target.checked)}
                    style={{ marginRight: 6 }}
                  />
                  Recordar esta cuenta para <strong>{proveedor.nombre}</strong>
                  {cuentaLabel && <span> ({cuentaLabel})</span>}
                </label>
              </div>
            )}
          </>
        )}

        {/* Vista de solo-lectura para facturas ya vinculadas y clasificadas (cerradas) */}
        {!esIgnorada && !cargando && esVinculadaConClasificacion && (
          <>
            {proveedor && (
              <div
                style={{
                  fontSize: "0.78rem",
                  marginBottom: 12,
                  padding: "8px 12px",
                  background: "#d5f5e3",
                  borderRadius: 4,
                  color: "#27ae60",
                }}
              >
                Proveedor identificado: <strong>{proveedor.nombre}</strong> (ID {proveedor.id})
              </div>
            )}

            {/* Clasificación contable actual (no editable) */}
            <div
              style={{
                border: "1px solid #dee2e6",
                borderRadius: 6,
                padding: "12px 14px",
                marginBottom: 12,
                background: "#fcfcfd",
                fontSize: "0.82rem",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "6px 28px",
                  marginBottom: activosVinculados.length > 0 ? 10 : 0,
                }}
              >
                <div>
                  <strong>Tipo contable:</strong>{" "}
                  <span style={{ textTransform: "capitalize" }}>{factura.tipo_contable || "—"}</span>
                </div>
                {factura.tipo_contable === "gasto" && (
                  <div>
                    <strong>Cuenta ER:</strong>{" "}
                    {CUENTAS_ER.find((c) => c.value === factura.cuenta_er)?.label || factura.cuenta_er || "—"}
                  </div>
                )}
              </div>

              {/* Líneas de activo (solo lectura) */}
              {activosVinculados.length > 0 && (
                <div style={{ fontSize: "0.78rem", color: "#555" }}>
                  <strong>
                    Líneas de activo: {activosVinculados.length} línea{activosVinculados.length > 1 ? "s" : ""}
                  </strong>
                  <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                    {activosVinculados.map((a) => {
                      const tipoLabel = TIPOS_ACTIVO.find((t) => t.value === a.tipo_activo)?.label || a.tipo_activo;
                      const destinoLabel =
                        a.destino_tipo === "edificio"
                          ? edificios.find((e) => e.id === a.destino_id)?.edificio || "Edificio"
                          : a.destino_tipo === "carteleria"
                            ? proyectosCarteleria.find((p) => p.id === a.destino_id)?.nombre_proyecto || "Cartelería"
                            : a.destino_tipo === "empleado"
                              ? getDestinoOpciones("empleado").find((o) => o.value === a.destino_empleado_id)?.label ||
                                "Empleado"
                              : null;
                      return (
                        <li key={a.id}>
                          {a.cantidad}x {tipoLabel}
                          {a.descripcion ? ` — ${a.descripcion}` : ""}
                          {destinoLabel ? ` → ${destinoLabel}` : ""}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>

            {/* Mensaje de factura cerrada */}
            <div
              style={{
                background: "#fff3cd",
                color: "#856404",
                padding: "10px 14px",
                borderRadius: 6,
                fontSize: "0.82rem",
                fontWeight: 600,
              }}
            >
              Esta factura está vinculada. Para editarla, primero desvinculala.
            </div>
          </>
        )}

        {/* Panel de confirmación (solo al asignar pendiente; nunca para ya vinculadas) */}
        {!esIgnorada && !esVinculada && confirmando && (
          <div
            style={{
              marginTop: 16,
              padding: "14px 16px",
              background: "#eaf2f8",
              borderRadius: 6,
              border: "1px solid #aed6f1",
              fontSize: "0.82rem",
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 8 }}>Confirmar asignación</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 28px" }}>
              <div>
                <strong>DTE Folio:</strong> {factura.folio} — {formatMonto(factura.monto_total)}
              </div>
              <div>
                <strong>Proveedor:</strong> {proveedor?.nombre || factura.razon_social_emisor}
              </div>
              <div>
                <strong>Cuenta ER:</strong> {cuentaLabel || cuentaER}
              </div>
              <div>
                <strong>Tipo:</strong> {tipoContable}
              </div>
            </div>
            {/* Resumen líneas de activo */}
            {tipoContable === "activo" && lineasActivo.length > 0 && (
              <div style={{ marginTop: 8, fontSize: "0.78rem", color: "#555" }}>
                <strong>Líneas de activo:</strong> {lineasActivo.length} línea{lineasActivo.length > 1 ? "s" : ""} —
                Total: {formatMonto(totalLineas)}
                <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                  {lineasActivo.map((l, i) => {
                    const tipoLabel = TIPOS_ACTIVO.find((t) => t.value === l.tipo_activo)?.label || l.tipo_activo;
                    return (
                      <li key={i}>
                        {l.cantidad}x {tipoLabel}
                        {l.descripcion ? ` — ${l.descripcion}` : ""}
                        {l.destino_tipo === "edificio"
                          ? ` → ${edificios.find((e) => e.id === l.destino_id)?.edificio || "Edificio"}`
                          : l.destino_tipo === "carteleria"
                            ? ` → ${proyectosCarteleria.find((p) => p.id === l.destino_id)?.nombre_proyecto || "Cartelería"}`
                            : l.destino_tipo === "empleado"
                              ? ` → ${getDestinoOpciones("empleado").find((o) => o.value === l.destino_id)?.label || "Empleado"}`
                              : " → Sin asignar"}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
              <label style={{ fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.78rem" }}>
                Pago estimado <span style={{ color: "#e74c3c" }}>*</span>
              </label>
              <input
                type="date"
                value={plazopago}
                onChange={(e) => setPlazopago(e.target.value)}
                style={{
                  height: 30,
                  fontSize: "0.78rem",
                  padding: "4px 8px",
                  border: `1px solid ${!plazopago ? "#e74c3c" : "#aed6f1"}`,
                  borderRadius: 4,
                }}
              />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, justifyContent: "flex-end" }}>
              <Button color="secondary" outline size="sm" onClick={() => setConfirmando(false)}>
                Volver
              </Button>
              <Button color="dark" size="sm" onClick={handleAsignar} disabled={guardando}>
                {guardando ? "Guardando..." : "Confirmar"}
              </Button>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {esIgnorada ? (
          <>
            <Button color="secondary" size="sm" onClick={toggle}>
              Cerrar
            </Button>
            <Button color="warning" size="sm" onClick={handleReactivar} disabled={guardando}>
              {guardando ? "..." : "Reactivar"}
            </Button>
          </>
        ) : confirmando || modoRapido ? (
          <Button
            color="secondary"
            outline
            size="sm"
            onClick={confirmando ? toggle : handleIgnorar}
            disabled={guardando}
          >
            {confirmando ? "Cerrar" : "Descartar"}
          </Button>
        ) : esVinculadaConClasificacion ? (
          // Vinculada + clasificada (cerrada): solo lectura. Para editar hay que desvincular primero.
          // Confirmación inline (sin window.confirm).
          confirmandoDesvincular ? (
            <>
              <span style={{ fontSize: "0.78rem", color: "#856404", marginRight: "auto" }}>
                ¿Desvincular la clasificación? El pago (si existe) no se toca.
              </span>
              <Button
                color="secondary"
                outline
                size="sm"
                onClick={() => setConfirmandoDesvincular(false)}
                disabled={desvinculando}
              >
                Cancelar
              </Button>
              <Button color="warning" size="sm" onClick={handleDesvincular} disabled={desvinculando}>
                {desvinculando ? "Desvinculando..." : "Sí, desvincular"}
              </Button>
            </>
          ) : (
            <>
              <Button color="secondary" size="sm" onClick={toggle}>
                Cerrar
              </Button>
              <Button color="warning" size="sm" onClick={() => setConfirmandoDesvincular(true)}>
                Desvincular
              </Button>
            </>
          )
        ) : esVinculada ? (
          // Vinculada SIN clasificar (pago existe, falta clasificación): editable, pero sin
          // "Pago estimado" — se guarda directo la clasificación.
          <>
            <Button color="secondary" outline size="sm" onClick={handleIgnorar} disabled={guardando}>
              Descartar
            </Button>
            <Button
              color="dark"
              size="sm"
              onClick={handleAsignar}
              disabled={guardando || !tipoContable || (tipoContable === "gasto" && !cuentaER)}
            >
              {guardando ? "Guardando..." : "Guardar"}
            </Button>
          </>
        ) : (
          <>
            <Button color="secondary" outline size="sm" onClick={handleIgnorar} disabled={guardando}>
              Descartar
            </Button>
            <Button
              color="dark"
              size="sm"
              onClick={() => setConfirmando(true)}
              disabled={guardando || !tipoContable || (tipoContable === "gasto" && !cuentaER)}
            >
              Asignar
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default AsignarRecibidaModal;
